import type { ICellEditorParams } from "ag-grid-community";
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { type ActionMeta, createFilter } from "react-select";
import Creatable from "react-select/creatable";

import { ColoredLabel } from "widgets";

import { getCssColor } from "../../../../../app-utils/color.js";
import { type IStringComparisonConfiguration, compareStrings } from "../../../../../app-utils/string.js";
import { type OrganismProperty } from "../../../../../graphql.js";
import { type IAppState } from "../../../../../redux/reducer.js";
import { TableCell } from "../core/TableCell.js";

interface ICreatableOrganismProperty extends Partial<OrganismProperty> {
  created?: boolean;
}

export const PHENOTYPE_COMPARISON_CONFIG: IStringComparisonConfiguration = {
  ignoreAccents: true,
  ignoreCase: true,
  trim: true,
};

export const PhenotypeCellEditor = forwardRef((props: ICellEditorParams, ref) => {
  const { value } = props;

  const existingValues = useSelector<IAppState, OrganismProperty[]>(
    (state) => state.dataEditing.orgPropertyValuesLookup[props.colDef.colId],
  );

  const initialOrgProperty: Partial<OrganismProperty> = existingValues.find(
    (phenotype) => phenotype.value === value,
  ) || {
    value: value,
  };

  const [tag, setTag] = useState<ICreatableOrganismProperty>(initialOrgProperty);
  const refInput = useRef(null);

  const c = useCallback(() => {
    return {
      // the final tag to send to the grid, on completion of editing
      getValue(): string {
        return tag.value;
      },
      afterGuiAttached: () => {
        refInput.current.focus();
      },
    };
  }, [tag]);

  /* Component Editor Lifecycle methods */
  useImperativeHandle(ref, c);

  const onChange = (value: ICreatableOrganismProperty, actionMeta: ActionMeta<ICreatableOrganismProperty>) => {
    const orgProperty: ICreatableOrganismProperty = { ...value, created: null };
    setTag(orgProperty);
    if (actionMeta.action === "select-option" || actionMeta.action === "create-option") {
      setTimeout(props.stopEditing, 0);
    }
  };

  return (
    <TableCell editor>
      <Creatable
        ref={refInput}
        className="react-select"
        classNamePrefix={"react-select"}
        isMulti={false}
        openMenuOnFocus={true}
        isSearchable={true}
        closeMenuOnSelect={true}
        isClearable={true}
        value={tag}
        defaultInputValue={props.charPress || "" /* initialOrgProperty?.value*/}
        getOptionValue={(option: ICreatableOrganismProperty) => option.value}
        options={existingValues}
        onChange={onChange}
        autoFocus={true}
        //blurInputOnSelect={true} // this makes the stopEditing happen too early, therefore the previous tag is selected
        onBlur={() => props.stopEditing()}
        tabSelectsValue={false}
        allowCreateWhileLoading={true}
        createOptionPosition={"first"}
        getNewOptionData={(inputValue): ICreatableOrganismProperty => {
          // do not suggest the creation of a new term if the value already exists (despite casing and triming)
          if (
            existingValues.find((existingValue) =>
              compareStrings(inputValue, existingValue.value, PHENOTYPE_COMPARISON_CONFIG),
            )
          ) {
            return null;
          }
          return { value: inputValue?.trim(), created: true };
        }}
        formatOptionLabel={(option: ICreatableOrganismProperty) => (
          <div style={{ display: "flex", alignItems: "baseline" }}>
            <span className="ellipsis-cell">
              <ColoredLabel color={!option.created && option.color ? getCssColor(option.color) : undefined}>
                {option.created ? `Create new '${option.value}'` : option.value}
              </ColoredLabel>
            </span>
          </div>
        )}
        filterOption={createFilter(PHENOTYPE_COMPARISON_CONFIG)}
        formatCreateLabel={(inputValue) => <div>Create fresh {inputValue}</div>} // does not work! therefore workaround with option.created
        components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }} // do not show the caret
        maxMenuHeight={200}
        menuPortalTarget={document.body}
        styles={{
          control: (base) => ({
            ...base,
            minHeight: 25, // resize the minHeight
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 650,
          }),
          menu: (base) => ({
            // let the menu extend its width to the longest option
            ...base,
            width: "max-content",
            minWidth: "100%",
          }),
        }}
      />
    </TableCell>
  );
});
